import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Card,
  TextField,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Pagination,
  Link,
  CircularProgress,
  styled,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import EditExpenseDialog from "./EditExpenseDialog";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

/* ------------ print rules ------------- */
const printStyles = `
  @media print {
    body *            { visibility: hidden; }
    .print-content,
    .print-content *  { visibility: visible; }
    .print-content    { position:absolute; left:0; top:0; width:100%; padding:20px; }
    .no-print         { display:none !important; }
  }
`;
/* --------------------------------------- */

/* --- styled helpers --- */
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    borderRadius: 8,
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
    backgroundColor: theme.palette.background.paper,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: "bold",
  borderRadius: 8,
  boxShadow: theme.shadows[3],
  transition: "all .25s",
  "&:hover": { boxShadow: theme.shadows[6], transform: "translateY(-2px)" },
  "&:active": { boxShadow: theme.shadows[2], transform: "translateY(0)" },
}));
/* ----------------------- */

const ExpensesList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const printRef = useRef(null);

  /* -------- filters -------- */
  const [selectedExpenseType, setSelectedExpenseType] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isDaily, setIsDaily] = useState(false);

  /* -------- pagination ----- */
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalExpenses, setTotalExpenses] = useState(0);

  /* -------- fetch lists ----- */
  const { data: expenseTypes } = useFetchObjects(
    ["expense-types"],
    "expense-types/",
    token
  );

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["expenses", page, rowsPerPage, selectedExpenseType, startDate, endDate],
    `expenses/?${
      selectedExpenseType ? `expenseType=${selectedExpenseType}&` : ""
    }${startDate ? `date_from=${startDate.toISOString().split("T")[0]}&` : ""}${
      endDate ? `date_to=${endDate.toISOString().split("T")[0]}&` : ""
    }page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("expenses", token);

  /* -------- local state ----- */
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  useEffect(() => {
    if (data?.results) setFilteredExpenses(data.results);
  }, [data]);

  useEffect(() => {
    refetch();
  }, [selectedExpenseType, startDate, endDate, page, rowsPerPage, refetch]);

  useEffect(() => {
    if (data?.count !== undefined) setTotalExpenses(data.count);
  }, [data]);

  const openEditDialog = (ex) => {
    setSelectedExpense(ex);
    setEditDialogOpen(true);
  };
  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedExpense(null);
    refetch();
  };

  const totalExpenseAmount = (Array.isArray(filteredExpenses)
    ? filteredExpenses
    : []
  ).reduce((sum, ex) => sum + (parseFloat(ex.amount) || 0), 0);

  /* -------- print handler ---- */
  const handlePrint = () => {
    const s = document.createElement("style");
    s.innerHTML = printStyles;
    document.head.appendChild(s);
    window.print();
    setTimeout(() => document.head.removeChild(s), 800);
  };

  return (
    <Grid container spacing={0} mb={4} pb={5}>
      {/* FILTER + ACTION BAR (no‑print) */}
      <Grid item xs={12} className="no-print">
        <Card sx={{ p: 2, mb: 3, boxShadow: "0px 2px 10px rgba(0,0,0,0.1)" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isXs ? "column" : "row",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              display="flex"
              alignItems="center"
            >
              <FilterAltIcon sx={{ mr: 1 }} /> {t("finance.filterExpenses")}
            </Typography>

            <Box display="flex" gap={2}>
              <IconButton onClick={handlePrint} color="primary">
                <PrintIcon />
              </IconButton>
              <StyledButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/finance/expenses/add")}
              >
                {t("finance.addNew")}
              </StyledButton>
            </Box>
          </Box>

          {/* controls */}
          <Grid container spacing={2} alignItems="center" mt={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("finance.expenseType")}</InputLabel>
                <Select
                  value={selectedExpenseType}
                  onChange={(e) => {
                    setSelectedExpenseType(e.target.value);
                    setPage(1);
                  }}
                  label={t("finance.expenseType")}
                >
                  <MenuItem value="">{t("finance.all")}</MenuItem>
                  {expenseTypes?.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* date pickers */}
            <Grid item xs={6} md={2}>
              <DatePicker
                selected={startDate}
                onChange={(d) => {
                  setStartDate(d);
                  setIsDaily(false);
                }}
                placeholderText={t("finance.startDate")}
                dateFormat="yyyy-MM-dd"
                customInput={<StyledTextField size="small" fullWidth />}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <DatePicker
                selected={endDate}
                onChange={(d) => {
                  setEndDate(d);
                  setIsDaily(false);
                }}
                placeholderText={t("finance.endDate")}
                dateFormat="yyyy-MM-dd"
                customInput={<StyledTextField size="small" fullWidth />}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <StyledButton
                variant={isDaily ? "contained" : "outlined"}
                fullWidth
                onClick={() => {
                  const today = new Date();
                  setStartDate(today);
                  setEndDate(today);
                  setIsDaily(true);
                  setPage(1);
                }}
              >
                {t("finance.apply")}
              </StyledButton>
            </Grid>
          </Grid>

          {/* selected range tag */}
          <Box mt={2}>
            <Typography variant="subtitle2" color="textSecondary">
              {startDate || endDate
                ? `${t("finance.startDate")}: ${
                    startDate?.toLocaleDateString() || "—"
                  }  —  ${t("finance.endDate")}: ${
                    endDate?.toLocaleDateString() || "—"
                  }`
                : t("finance.allDates")}
            </Typography>
          </Box>
        </Card>
      </Grid>

      {/* PRINTABLE AREA (range header + table) */}
      <Grid item xs={12}>
        <Box ref={printRef} className="print-content">
          {/* range header for print */}
          <Box sx={{ mb: 2, pl: 1 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {startDate || endDate
                ? `${t("finance.startDate")}: ${
                    startDate?.toLocaleDateString() || "—"
                  }  —  ${t("finance.endDate")}: ${
                    endDate?.toLocaleDateString() || "—"
                  }`
                : t("finance.allDates")}
            </Typography>
          </Box>

          {/* table section */}
          {isLoading ? (
            <Box textAlign="center" mt={4}>
              <CircularProgress />
              <Typography variant="body2">{t("finance.loading")}</Typography>
            </Box>
          ) : isError ? (
            <Box textAlign="center" color="error.main" mt={4}>
              <Typography variant="body2">
                {t("finance.errorFetchingData")}
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ boxShadow: "0px 2px 10px rgba(0,0,0,0.1)" }}
            >
              <Table>
                <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                  <TableRow>
                    <TableCell align="center">{t("finance.id")}</TableCell>

                    <TableCell align="center">
                      {t("finance.expenseType")}
                    </TableCell>
                    <TableCell align="center">{t("finance.amount")}</TableCell>
                    <TableCell align="center">{t("finance.totalPaid")}</TableCell>
                    <TableCell align="center">
                      {t("finance.remainingBalance")}
                    </TableCell>
                    <TableCell align="center">
                      {t("finance.description")}
                    </TableCell>
                    <TableCell align="center">{t("finance.file")}</TableCell>
                    <TableCell align="center" className="no-print">
                      {t("finance.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredExpenses.length ? (
                    filteredExpenses.map((ex) => (
                      <TableRow key={ex.id}>
                        <TableCell align="center">{ex.id}</TableCell>

                        <TableCell align="center">
                          {ex.expenseType_display}
                        </TableCell>
                        <TableCell align="center">{ex.amount}</TableCell>
                        <TableCell align="center">
                          {Number(ex.total_paid || 0).toLocaleString()} ؋
                        </TableCell>
                        <TableCell align="center">
                          {Number(ex.remaining_balance || 0).toLocaleString()} ؋
                        </TableCell>
                        <TableCell align="center">{ex.description}</TableCell>
                        <TableCell align="center">
                          {ex.file ? (
                            <Link href={ex.file} target="_blank" rel="noopener">
                              {t("finance.downloadFile")}
                            </Link>
                          ) : (
                            t("finance.noFile")
                          )}
                        </TableCell>
                        <TableCell align="center" className="no-print">
                          <IconButton
                            onClick={() => openEditDialog(ex)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(ex.id)}
                            color="secondary"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography variant="h6">
                          {t("finance.noExpenseSourceFound")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell colSpan={5} />
                    <TableCell align="right" colSpan={2}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t("finance.total")}: {Number(totalExpenseAmount || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* pagination (screen only) */}
        {!isLoading && !isError && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={4}
            p={2}
            className="no-print"
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body2" mr={1}>
                {t("finance.rowsPerPage")}:
              </Typography>
              <FormControl size="small">
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(+e.target.value);
                    setPage(1);
                  }}
                  sx={{ width: 80 }}
                >
                  {[10, 25, 50, 100, 500, 1000].map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalExpenses / rowsPerPage)}
              page={page}
              onChange={(_, p) => setPage(p)}
              variant="outlined"
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Grid>

      {/* dialogs */}
      <ConfirmDialog />
      {selectedExpense && (
        <EditExpenseDialog
          open={editDialogOpen}
          onClose={closeEditDialog}
          expense={selectedExpense}
          token={token}
        />
      )}
    </Grid>
  );
};

export default ExpensesList;
